// 2D preview of a single cabinet panel, rendered inside PartEditor.
//
// Renders the part as a rectangle in its as-laid-on-the-saw orientation
// (w horizontal, h vertical), with:
//   • the material-tinted panel fill
//   • coloured strokes on the banded edges (top / bottom / left / right)
//   • a grain arrow in the centre if the part has a length/width grain
//   • every standard drilling as a circle, semi-transparent + dashed
//     when suppressed
//   • custom (user-added) drillings as a thicker orange ring with a
//     centre cross, distinct from standard holes
//
// Interactions:
//   • click a standard drilling → toggles its suppression (same as the
//     checkbox in the standard-drillings list below the preview).
//   • click a custom drilling → calls onFocusExtra(idx) so the parent
//     can scroll to / highlight its row in the custom-drillings table.
//
// SVG Y is flipped because the saw frame has Y running bottom→top
// while SVG has top→bottom; that way the part renders "right side up"
// to a designer who's used to looking at panels on the bench.

import type { CSSProperties } from "react";
import type { CabinetPart, CustomDrilling, EdgeBand, EdgeSet, Id } from "@/lib/types";
import type { DrillEntry } from "@/lib/cabinets";

const KIND_FILL: Record<DrillEntry["kind"], string> = {
  shelf_pin: "#9aa3ad",
  hinge_cup: "#c4a06c",
  screw:     "#7aa6d6",
  cable:     "#6ec48b",
  custom:    "#ffaa55",
  groove:    "#8b6a3a",
};

const PANEL_FILL = "#dec3a4";       // carcass default; could become role-driven later
const NO_BAND   = "rgba(0,0,0,0.18)";

interface Props {
  part: CabinetPart;
  /** Standard drillings, indexed identically to the toggle list. */
  stdDrillings: DrillEntry[];
  /** User-added drillings. */
  extras: CustomDrilling[];
  /** Set of standard-drilling indices that have been suppressed. */
  suppressedSet: Set<number>;
  /** Project edge bands — used to resolve each banded edge's colour. */
  edgeBands: EdgeBand[];
  /** Toggle a standard drilling's suppression (matches the row checkbox). */
  onToggleSuppress: (idx: number) => void;
  /** Focus a custom drilling row in the table below the preview. */
  onFocusExtra?: (idx: number) => void;
  /** Optional override of SVG height (default 220px). */
  height?: number;
  /** Auto-derived drillings that aren't in the class pattern (drawer
   *  slide-rail screws live on the cabinet, not the class). Rendered
   *  like standard circles but non-clickable — they can't be suppressed
   *  through the standard-drilling toggle list. */
  autoDrillings?: DrillEntry[];
}

export function PartPreview({
  part, stdDrillings, extras, suppressedSet, edgeBands,
  onToggleSuppress, onFocusExtra, height = 220,
  autoDrillings = [],
}: Props) {
  const W = Math.max(0.001, part.w);
  const H = Math.max(0.001, part.h);
  const T = part.thickness;
  const bandLookup = new Map(edgeBands.map((b) => [b._id, b]));

  const margin = Math.max(0.5, Math.min(W, H) * 0.05);
  const viewW = W + 2 * margin;
  const viewH = H + 2 * margin;

  // Edge stroke widths: scale to part so a 1mm strip is visible on a
  // wall cabinet but not absurd on a tiny stretcher. Floor at 0.05".
  const edgeStroke = Math.max(0.05, Math.min(W, H) * 0.012);
  const edgeColour = (id: Id | null | undefined): string => {
    if (id == null) return NO_BAND;
    const band = bandLookup.get(id);
    return band?.color ?? "var(--accent, #ffaa55)";
  };

  return (
    <section style={containerStyle}>
      <div style={headerStyle}>
        <span className="label" style={{ margin: 0 }}>Panel preview</span>
        <span className="muted" style={{ fontSize: 10, flex: 1, textAlign: "right" }}>
          {W.toFixed(2)}″ × {H.toFixed(2)}″ × {T.toFixed(3)}″ ·
          {" "}{stdDrillings.length - suppressedSet.size}/{stdDrillings.length} std
          {autoDrillings.length > 0 && ` · ${autoDrillings.length} auto`}
          {extras.length > 0 && ` · ${extras.length} extra`}
        </span>
      </div>

      <svg
        viewBox={`${-margin} ${-margin} ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height, display: "block" }}
        aria-label={`Preview of ${part.name}`}
      >
        {/* Panel body — flip Y so the saw frame reads naturally. */}
        <g transform={`translate(0, ${H}) scale(1, -1)`}>
          <rect
            x={0} y={0} width={W} height={H}
            fill={PANEL_FILL}
            stroke="rgba(0,0,0,0.5)"
            strokeWidth={Math.max(0.03, edgeStroke * 0.4)}
          />

          {/* Edge banding — coloured stroke per side, transparent grey
              for unbanded sides so the user still sees the four edges. */}
          <EdgeStroke side="t" w={W} h={H} stroke={edgeColour(part.edges.t)} thickness={edgeStroke} />
          <EdgeStroke side="b" w={W} h={H} stroke={edgeColour(part.edges.b)} thickness={edgeStroke} />
          <EdgeStroke side="l" w={W} h={H} stroke={edgeColour(part.edges.l)} thickness={edgeStroke} />
          <EdgeStroke side="r" w={W} h={H} stroke={edgeColour(part.edges.r)} thickness={edgeStroke} />

          <GrainArrow w={W} h={H} grain={part.grain} />

          {/* Standard drillings — circles for point holes, rectangles
              along (x,y)→(endX,endY) for grooves. */}
          {stdDrillings.map((d, i) => {
            const off = suppressedSet.has(i);
            if (d.kind === "groove" && d.endX != null && d.endY != null) {
              // Routed slot — render as rectangle of width=`diameter`
              // along the start→end vector.
              const dx = d.endX - d.x;
              const dy = d.endY - d.y;
              const len = Math.hypot(dx, dy);
              if (len < 1e-6) return null;
              const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
              const slotW = Math.max(0.05, d.diameter);
              return (
                <g key={`std-${i}`} onClick={() => onToggleSuppress(i)} style={{ cursor: "pointer" }}>
                  <rect
                    x={d.x} y={d.y - slotW / 2}
                    width={len} height={slotW}
                    fill={KIND_FILL.groove}
                    fillOpacity={off ? 0.15 : 0.45}
                    stroke={off ? "rgba(255,80,80,0.7)" : KIND_FILL.groove}
                    strokeWidth={Math.max(0.04, edgeStroke * 0.5)}
                    strokeDasharray={off ? "0.2,0.12" : "0.3,0.15"}
                    transform={`rotate(${angleDeg} ${d.x} ${d.y})`}
                  >
                    <title>
                      {`groove ⌀${(d.diameter * 25.4).toFixed(1)}mm wide, depth ${(d.depth * 25.4).toFixed(1)}mm @ (${d.x.toFixed(2)}″, ${d.y.toFixed(2)}″) → (${d.endX.toFixed(2)}″, ${d.endY.toFixed(2)}″) face ${d.face}${off ? " — SUPPRESSED" : ""}`}
                    </title>
                  </rect>
                </g>
              );
            }
            return (
              <g key={`std-${i}`} onClick={() => onToggleSuppress(i)} style={{ cursor: "pointer" }}>
                <circle
                  cx={d.x} cy={d.y}
                  r={Math.max(0.05, d.diameter / 2)}
                  fill={KIND_FILL[d.kind]}
                  fillOpacity={off ? 0.18 : 0.9}
                  stroke={off ? "rgba(255,80,80,0.7)" : "rgba(0,0,0,0.6)"}
                  strokeWidth={Math.max(0.02, edgeStroke * 0.4)}
                  strokeDasharray={off ? "0.15,0.1" : undefined}
                >
                  <title>
                    {`${d.kind} ⌀${(d.diameter * 25.4).toFixed(1)}mm depth ${(d.depth * 25.4).toFixed(1)}mm @ (${d.x.toFixed(2)}″, ${d.y.toFixed(2)}″) face ${d.face}${off ? " — SUPPRESSED" : ""}`}
                  </title>
                </circle>
              </g>
            );
          })}

          {/* Auto drillings (e.g. drawer slide screws) — same look as
              standard circles but no click handler, since they aren't
              part of the class's suppressible pattern. */}
          {autoDrillings.map((d, i) => (
            <g key={`auto-${i}`} pointerEvents="none">
              <circle
                cx={d.x} cy={d.y}
                r={Math.max(0.05, d.diameter / 2)}
                fill={KIND_FILL[d.kind]}
                fillOpacity={0.9}
                stroke="rgba(0,0,0,0.6)"
                strokeWidth={Math.max(0.02, edgeStroke * 0.4)}
              >
                <title>
                  {`${d.kind} (auto) ⌀${(d.diameter * 25.4).toFixed(1)}mm depth ${(d.depth * 25.4).toFixed(1)}mm @ (${d.x.toFixed(2)}″, ${d.y.toFixed(2)}″) face ${d.face}`}
                </title>
              </circle>
            </g>
          ))}

          {/* Custom drillings — thicker ring + cross to distinguish. */}
          {extras.map((d, i) => {
            const r = Math.max(0.06, d.diameter / 2);
            const cross = r * 0.7;
            return (
              <g
                key={`ex-${i}`}
                onClick={() => onFocusExtra?.(i)}
                style={{ cursor: onFocusExtra ? "pointer" : "default" }}
              >
                <circle
                  cx={d.x} cy={d.y} r={r}
                  fill="none"
                  stroke={KIND_FILL[d.kind] ?? KIND_FILL.custom}
                  strokeWidth={Math.max(0.04, edgeStroke * 0.7)}
                />
                <line x1={d.x - cross} y1={d.y} x2={d.x + cross} y2={d.y}
                      stroke={KIND_FILL[d.kind] ?? KIND_FILL.custom}
                      strokeWidth={Math.max(0.03, edgeStroke * 0.5)} />
                <line x1={d.x} y1={d.y - cross} x2={d.x} y2={d.y + cross}
                      stroke={KIND_FILL[d.kind] ?? KIND_FILL.custom}
                      strokeWidth={Math.max(0.03, edgeStroke * 0.5)} />
                <title>
                  {`[${d.kind}] custom ⌀${(d.diameter * 25.4).toFixed(1)}mm depth ${(d.depth * 25.4).toFixed(1)}mm @ (${d.x.toFixed(2)}″, ${d.y.toFixed(2)}″) face ${d.face}`}
                </title>
              </g>
            );
          })}
        </g>
      </svg>

      <EdgeLegend edges={part.edges} edgeBands={edgeBands} />
    </section>
  );
}

function EdgeStroke({
  side, w, h, stroke, thickness,
}: {
  side: "t" | "b" | "l" | "r";
  w: number;
  h: number;
  stroke: string;
  thickness: number;
}) {
  let x1 = 0, y1 = 0, x2 = 0, y2 = 0;
  if (side === "t") { x1 = 0; y1 = h - thickness / 2; x2 = w; y2 = h - thickness / 2; }
  if (side === "b") { x1 = 0; y1 = thickness / 2;     x2 = w; y2 = thickness / 2;     }
  if (side === "l") { x1 = thickness / 2;     y1 = 0; x2 = thickness / 2;     y2 = h; }
  if (side === "r") { x1 = w - thickness / 2; y1 = 0; x2 = w - thickness / 2; y2 = h; }
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={stroke}
      strokeWidth={thickness}
      strokeLinecap="butt"
    />
  );
}

function GrainArrow({ w, h, grain }: { w: number; h: number; grain: CabinetPart["grain"] }) {
  if (grain === "none") return null;
  const cx = w / 2;
  const cy = h / 2;
  const len = Math.min(w, h) * 0.3;
  const head = Math.min(w, h) * 0.04;
  const colour = "rgba(0,0,0,0.35)";
  const stroke = Math.max(0.04, Math.min(w, h) * 0.008);

  const horizontal = grain === "length";
  if (horizontal) {
    return (
      <g pointerEvents="none">
        <line x1={cx - len} y1={cy} x2={cx + len} y2={cy} stroke={colour} strokeWidth={stroke} />
        <polygon
          points={`${cx + len},${cy} ${cx + len - head},${cy + head} ${cx + len - head},${cy - head}`}
          fill={colour}
        />
        <polygon
          points={`${cx - len},${cy} ${cx - len + head},${cy + head} ${cx - len + head},${cy - head}`}
          fill={colour}
        />
      </g>
    );
  }
  return (
    <g pointerEvents="none">
      <line x1={cx} y1={cy - len} x2={cx} y2={cy + len} stroke={colour} strokeWidth={stroke} />
      <polygon
        points={`${cx},${cy + len} ${cx + head},${cy + len - head} ${cx - head},${cy + len - head}`}
        fill={colour}
      />
      <polygon
        points={`${cx},${cy - len} ${cx + head},${cy - len + head} ${cx - head},${cy - len + head}`}
        fill={colour}
      />
    </g>
  );
}

function EdgeLegend({ edges, edgeBands }: { edges: EdgeSet; edgeBands: EdgeBand[] }) {
  const bandLookup = new Map(edgeBands.map((b) => [b._id, b]));
  const rows: { side: string; band: EdgeBand | undefined }[] = [
    { side: "T", band: edges.t != null ? bandLookup.get(edges.t) : undefined },
    { side: "B", band: edges.b != null ? bandLookup.get(edges.b) : undefined },
    { side: "L", band: edges.l != null ? bandLookup.get(edges.l) : undefined },
    { side: "R", band: edges.r != null ? bandLookup.get(edges.r) : undefined },
  ];
  if (rows.every((r) => !r.band)) return null;
  return (
    <div style={{ display: "flex", gap: 10, fontSize: 10, color: "var(--fg-dim)", flexWrap: "wrap" }}>
      {rows.map((r) => (
        <span key={r.side} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontFamily: "ui-monospace, monospace", minWidth: 12 }}>{r.side}</span>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: r.band?.color ?? "transparent", border: r.band ? "none" : "1px dashed var(--fg-dim)" }} />
          <span>{r.band?.name ?? "—"}</span>
        </span>
      ))}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────

const containerStyle: CSSProperties = {
  border: "1px solid var(--line)",
  borderRadius: 4,
  padding: 8,
  background: "rgba(255,255,255,0.02)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};
const headerStyle: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
};
